/**
 * 装配体基准引用依赖求解诊断。
 *
 * 文件职责：
 * 1. 把派生基准依赖快照归一为标准 Mate 求解器诊断；
 * 2. 只阻断引用缺失等确定不可恢复场景；
 * 3. 保持诊断文案在 cad-core 内统一输出，web-client 只负责展示。
 */

import {
  AssemblyReferenceDependencyKind,
  MateSolveStatus,
  type MateEntityRef,
} from '../definition';

/**
 * Mate 应用阶段可提前返回的依赖诊断。
 */
export interface AssemblyReferenceDependencySolveIssue {
  /** 求解状态。 */
  readonly status: MateSolveStatus;
  /** 诊断文案。 */
  readonly message: string;
}

/**
 * 从实体依赖快照解析求解阻断诊断。
 *
 * @param entity Mate 实体引用。
 * @returns 阻断诊断；依赖可求解时返回 `null`。
 */
function resolveEntityReferenceDependencySolveIssue(
  entity: MateEntityRef,
): AssemblyReferenceDependencySolveIssue | null {
  const dependency = entity.referenceDependency;
  if (!dependency) {
    return null;
  }
  if (dependency.kind === AssemblyReferenceDependencyKind.MissingReference) {
    return {
      status: MateSolveStatus.MissingReference,
      message: dependency.message ?? '无法恢复派生基准的参考对象，配合引用缺失。',
    };
  }
  return null;
}

/**
 * 解析 Mate 中所有实体的基准依赖阻断诊断。
 *
 * @param mate Mate Definition。
 * @returns 阻断诊断；依赖可求解时返回 `null`。
 */
export function resolveAssemblyReferenceDependencySolveIssue(
  mate: {
    readonly firstEntity: MateEntityRef;
    readonly secondEntity: MateEntityRef;
    readonly referenceEntity?: MateEntityRef;
  },
): AssemblyReferenceDependencySolveIssue | null {
  return resolveEntityReferenceDependencySolveIssue(mate.firstEntity)
    ?? resolveEntityReferenceDependencySolveIssue(mate.secondEntity)
    ?? (mate.referenceEntity ? resolveEntityReferenceDependencySolveIssue(mate.referenceEntity) : null);
}
